import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-2 border-transparent text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:border-focus disabled:pointer-events-none disabled:opacity-50 cursor-pointer [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-button-primary text-button-primary-foreground hover:bg-button-primary-hover active:bg-button-primary-pressed disabled:opacity-100 disabled:bg-button-disabled disabled:text-button-disabled-foreground',
        // DS secondary — outlined: 1px border, transparent bg, primary-tone text,
        // subtle fill on hover, 2px blue border on focus (via base + this width bump).
        secondary:
          'border border-button-secondary-border bg-transparent text-button-secondary-foreground hover:bg-button-secondary-hover active:border-button-secondary-border-pressed active:bg-button-secondary-pressed focus-visible:border-2 disabled:opacity-100 disabled:border-button-border-disabled disabled:bg-transparent disabled:text-button-disabled-foreground',
        // DS ghost — transparent default, subtle fill on hover, 1px border on
        // pressed; blue 2px focus comes from the shared base.
        ghost:
          'text-button-secondary-foreground hover:bg-button-ghost-hover active:border-button-secondary-border-pressed disabled:opacity-100 disabled:text-button-disabled-foreground',
        // DS destructive — outlined red: 1px red border, transparent bg, red
        // text, red-tint fill on hover/pressed; blue 2px focus from the base.
        destructive:
          'border border-button-destructive-border bg-transparent text-button-destructive-foreground hover:bg-button-destructive-hover active:bg-button-destructive-pressed focus-visible:border-2 disabled:opacity-100 disabled:border-button-border-disabled disabled:bg-transparent disabled:text-button-disabled-foreground',
      },
      size: {
        default: 'h-8 px-3',
        sm: 'h-6 px-2',
        lg: 'h-10 px-6',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  ),
);
Button.displayName = 'Button';

export { buttonVariants };
